import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

const SERVICES = [
  { id: 'standard', name: 'Standardstädning' },
  { id: 'deep', name: 'Djupstädning' },
  { id: 'moving', name: 'Flyttstädning' },
  { id: 'office', name: 'Kontorsstädning' },
  { id: 'window', name: 'Fönsterputs' },
  { id: 'help', name: 'Flytthjälp' },
  { id: 'minipaket', name: 'Minipaket' },
  { id: 'package', name: 'Månadspaket' }
];
const SUBS = [
  { id: 'once', name: 'Engångsstädning', pct: 0 },
  { id: 'weekly', name: 'Varje vecka (-15%)', pct: 15 },
  { id: 'biweekly', name: 'Varannan vecka (-10%)', pct: 10 },
  { id: 'triweekly', name: 'Var tredje vecka (-5%)', pct: 5 }
];
const SUB_SERVICES = ['standard', 'deep', 'office', 'window'];
const RATES = { standard: 65, deep: 80, moving: 95, office: 75 };
const MIN_PRICES = { standard: 3490, deep: 5490, moving: 7490, office: 4490 };

const num = (v) => parseFloat(v) || 0;
const pad = (n) => String(n).padStart(2, '0');
const toStr = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
const today = () => toStr(new Date());
const addDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return toStr(new Date(y, m - 1, d + n)); };
const likeSafe = (s) => (s || '').replace(/[\\%_]/g, (m) => '\\' + m);
const randomPassword = () => {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const arr = new Uint32Array(10);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join('');
};

const calcBase = (f) => {
  const sqm = parseInt(f.sqm, 10) || 0;
  const subPct = (SUBS.find(s => s.id === f.subscription) || SUBS[0]).pct;
  if (f.service === 'package') return Math.max(sqm * 250, 12500);
  if (f.service === 'minipaket') return Math.max(sqm * 135, 6750);
  if (f.service === 'help') return Math.max(num(f.hours) * 450, 2250);
  if (f.service === 'window') return Math.max(num(f.windowKr), 1599) * (1 - subPct / 100);
  return Math.max(sqm * RATES[f.service], MIN_PRICES[f.service]) * (SUB_SERVICES.includes(f.service) ? 1 - subPct / 100 : 1);
};

const box = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '16px', padding: '20px', marginBottom: '16px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '14px' };
const smallBtn = { padding: '8px 14px', borderRadius: '10px', backgroundColor: '#1a1a2e', color: '#C9A84C', fontWeight: 'bold', fontSize: '13px', border: 'none', cursor: 'pointer' };

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>{label}</label>
    {children}
  </div>
);

const buildInitial = (customer, lastBooking, eb) => {
  if (eb) {
    const d = eb.details || {};
    return {
      name: eb.name || '', email: eb.email || '', phone: eb.phone || '', personnummer: eb.personnummer || '',
      address: eb.address || '', city: eb.city || 'Motala', postalCode: eb.postal_code || '',
      service: eb.service || 'standard', subscription: d.subscription || 'once',
      sqm: eb.sqm ? String(eb.sqm) : '', hours: d.hours ? String(d.hours) : '',
      dates: d.dates && d.dates.length ? d.dates : [eb.date], time: eb.time || '09:00',
      status: eb.status || 'completed',
      needsEquipment: !!d.needsEquipment,
      ovenCount: d.ovenCount ? String(d.ovenCount) : '', fridgeCount: d.fridgeCount ? String(d.fridgeCount) : '',
      windowKr: d.windowKr ? String(d.windowKr) : '', windowNote: d.windowNote || '',
      otherKr: d.otherKr ? String(d.otherKr) : '', otherNote: d.otherNote || '',
      hasPets: !!d.hasPets, petText: (d.petTypes || [])[0] || '', keyHandling: d.keyHandling || 'home',
      rut: (eb.rut_discount || 0) > 0,
      discountPercent: d.discountPercent ? String(d.discountPercent) : '', discountCode: d.discountCode || '',
      staffIds: (d.staff || []).map(s => s.id), staffHours: d.staffHours ? String(d.staffHours) : '',
      notes: eb.notes || ''
    };
  }
  return {
    name: customer?.name || '', email: customer?.email || '', phone: customer?.phone || '',
    personnummer: lastBooking?.personnummer || '',
    address: customer?.address || lastBooking?.address || '',
    city: customer?.city || lastBooking?.city || 'Motala',
    postalCode: customer?.postal_code || lastBooking?.postal_code || '',
    service: 'standard', subscription: 'once', sqm: '', hours: '',
    dates: [today()], time: '09:00', status: 'completed',
    needsEquipment: false, ovenCount: '', fridgeCount: '',
    windowKr: '', windowNote: '', otherKr: '', otherNote: '',
    hasPets: false, petText: '', keyHandling: 'home',
    rut: true, discountPercent: '', discountCode: '',
    staffIds: [], staffHours: '', notes: ''
  };
};

const ManualBookingForm = ({ customer, lastBooking, editBooking, onSaved, onCancel }) => {
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [priceOverride, setPriceOverride] = useState(() =>
    editBooking?.details?.priceBeforeDiscount ? String(editBooking.details.priceBeforeDiscount) : ''
  );
  const [f, setF] = useState(() => buildInitial(customer, lastBooking, editBooking));
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  useEffect(() => {
    supabase.from('employees').select('id, name, email').order('name')
      .then(({ data }) => setEmployees(data || []));
  }, []);

  const isPackage = f.service === 'package' || f.service === 'minipaket';
  const isMulti = isPackage || (SUB_SERVICES.includes(f.service) && f.subscription !== 'once');
  const cleanDates = [...new Set(f.dates.filter(Boolean))].sort();
  const effectiveDates = isMulti ? cleanDates : (f.dates[0] ? [f.dates[0]] : []);
  const visits = isMulti && !isPackage ? Math.max(1, effectiveDates.length) : 1;

  const step = (f.service === 'minipaket' || f.subscription === 'biweekly') ? 14 : f.subscription === 'triweekly' ? 21 : 7;
  const autoCount = f.service === 'minipaket' ? 2 : 4;
  const setDate = (i, v) => set('dates', f.dates.map((x, j) => (j === i ? v : x)));
  const removeDate = (i) => set('dates', f.dates.filter((_, j) => j !== i));
  const addDate = () => set('dates', [...f.dates, addDays(f.dates[f.dates.length - 1] || today(), step)]);
  const fillDates = () => {
    const start = f.dates[0] || today();
    set('dates', Array.from({ length: autoCount }, (_, i) => addDays(start, step * i)));
  };

  const addons =
    (f.service !== 'window' && f.service !== 'help' && f.needsEquipment ? 400 : 0) +
    num(f.ovenCount) * 490 + num(f.fridgeCount) * 365 +
    (f.service === 'window' ? 0 : num(f.windowKr)) + num(f.otherKr);
  const autoPrice = Math.round(calcBase(f) * visits + addons);
  const priceBefore = priceOverride !== '' ? num(priceOverride) : autoPrice;
  const dPct = Math.min(100, Math.max(0, num(f.discountPercent)));
  const price = priceBefore * (1 - dPct / 100);
  const rutAmount = f.rut ? price * 0.5 : 0;
  const finalPrice = price - rutAmount;

  const toggleStaff = (id) =>
    set('staffIds', f.staffIds.includes(id) ? f.staffIds.filter(x => x !== id) : [...f.staffIds, id]);

  const lookupCode = async () => {
    const { data } = await supabase.from('discount_codes').select('discount_percent')
      .eq('code', f.discountCode.toUpperCase().trim()).limit(1);
    if (data && data[0]) set('discountPercent', String(data[0].discount_percent));
    else alert('Hittade ingen rabattkod med det namnet');
  };

  const save = async (sendMail = false) => {
    if (!f.name.trim() || effectiveDates.length === 0) { alert('Fyll i namn och minst ett datum'); return; }
    const email = f.email.trim().toLowerCase();
    if (sendMail && !email) { alert('Fyll i kundens email för att kunna skicka'); return; }
    setSaving(true);
    try {
      const staff = employees.filter(e => f.staffIds.includes(e.id));
      const serviceName = (SERVICES.find(s => s.id === f.service) || {}).name || f.service;
      const id = editBooking ? editBooking.id : `booking_${Date.now()}`;
      const priceR = Math.round(price);
      const rutR = Math.round(rutAmount);
      const details = {
        manual: true, service: f.service, subscription: f.subscription, sqm: f.sqm, hours: f.hours,
        dates: effectiveDates, visits, date: effectiveDates[0], time: f.time,
        needsEquipment: f.needsEquipment, ovenCount: num(f.ovenCount), fridgeCount: num(f.fridgeCount),
        windowKr: num(f.windowKr), windowNote: f.windowNote, otherKr: num(f.otherKr), otherNote: f.otherNote,
        hasPets: f.hasPets, petTypes: f.petText ? [f.petText] : [], keyHandling: f.keyHandling,
        notes: f.notes, discountPercent: dPct, discountCode: f.discountCode,
        staff: staff.map(s => ({ id: s.id, name: s.name })), staffHours: num(f.staffHours),
        priceBeforeDiscount: priceBefore
      };
      const row = {
        name: f.name.trim(), email, phone: f.phone, address: f.address, city: f.city,
        postal_code: f.postalCode, service: f.service, sqm: parseInt(f.sqm, 10) || null,
        date: effectiveDates[0], time: f.time, notes: f.notes,
        price: priceR, rut_discount: rutR, final_price: priceR - rutR,
        personnummer: f.personnummer, status: f.status,
        max_workers: staff.length || 2, accepted_count: staff.length, details
      };

      if (editBooking) {
        const { error } = await supabase.from('bookings').update(row).eq('id', id);
        if (error) throw error;
        await supabase.from('shift_acceptances').delete().eq('booking_id', id);
        await supabase.from('payroll_sessions').delete().eq('booking_id', id);
        const od = editBooking.details || {};
        const oldName = (SERVICES.find(s => s.id === (od.service || editBooking.service)) || {}).name;
        await supabase.from('payroll_sessions').delete().is('booking_id', null)
          .eq('date', editBooking.date).eq('note', `${oldName} - ${editBooking.name} (manuell)`);
      } else {
        const { error } = await supabase.from('bookings').insert([{ ...row, id, created_at: new Date().toISOString() }]);
        if (error) throw error;
      }

      if (staff.length > 0) {
        const ts = Date.now();
        const { error: accErr } = await supabase.from('shift_acceptances').insert(staff.map((s, i) => ({
          id: `acceptance_${ts}_${i}`, booking_id: id, employee_id: s.id,
          employee_name: s.name, employee_email: s.email, accepted_at: new Date().toISOString()
        })));
        if (accErr) throw accErr;
        const hrs = num(f.staffHours);
        if (hrs > 0) {
          const sessions = [];
          staff.forEach(s => effectiveDates.forEach(dt => sessions.push({
            employee_id: s.id, employee_name: s.name, date: dt, hours: hrs, hourly_rate: 165,
            total_wage: hrs * 165, note: `${serviceName} - ${f.name.trim()} (manuell)`, booking_id: id
          })));
          const { error: payErr } = await supabase.from('payroll_sessions').insert(sessions);
          if (payErr) throw payErr;
        }
      }

      let newAccount = null;
      let accountError = '';
      if (email) {
        try {
          const { data: existing } = await supabase.from('users').select('id').ilike('email', likeSafe(email)).limit(1);
          if (!existing || existing.length === 0) {
            const password = randomPassword();
            const { error: uErr } = await supabase.from('users').insert([{
              id: `user_${Date.now()}`, name: f.name.trim(), email, phone: f.phone, address: f.address,
              city: f.city, postal_code: f.postalCode, password, role: 'customer', created_by_admin: true
            }]);
            if (uErr) throw uErr;
            newAccount = { email, password, name: f.name.trim() };
          }
        } catch (e) {
          accountError = '⚠️ Bokningen sparades men kundkontot kunde inte skapas: ' + (e.message || 'okänt fel');
        }
      }

      let mailMsg = '';
      if (sendMail) {
        const res = await fetch('/.netlify/functions/send-manual-booking', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookingId: id, updated: !!editBooking })
        });
        const out = await res.json().catch(() => ({}));
        mailMsg = res.ok
          ? `✅ Mail skickat till ${email}`
          : '⚠️ Bokningen sparades men mailet misslyckades: ' + (typeof out.error === 'string' ? out.error : JSON.stringify(out.error || 'okänt fel'));
      }

      setSaving(false);
      onSaved({ newAccount, accountError, mailMsg });
    } catch (e) {
      console.error(e);
      setSaving(false);
      alert('Kunde inte spara: ' + (e.message || 'okänt fel'));
    }
  };

  return (
    <div className="max-w-3xl" style={{ color: '#1a1a2e' }}>
      <h2 className="text-2xl font-bold mb-1">{editBooking ? 'Redigera manuell bokning' : 'Ny manuell bokning'}</h2>
      <p className="text-sm mb-6" style={{ color: '#888' }}>
        {customer ? `Kund: ${customer.name}` : 'Ingen kundprofil vald — fyll i uppgifterna själv'}
      </p>

      <div style={box}>
        <h3 className="font-bold mb-1">Kund</h3>
        <p className="text-xs mb-3" style={{ color: '#888' }}>Finns inte kunden i systemet skapas en kundprofil automatiskt när du sparar (kräver email).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Namn"><input style={inp} value={f.name} onChange={e => set('name', e.target.value)} /></Field>
          <Field label="Email"><input style={inp} value={f.email} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="Telefon"><input style={inp} value={f.phone} onChange={e => set('phone', e.target.value)} /></Field>
          <Field label="Personnummer"><input style={inp} value={f.personnummer} onChange={e => set('personnummer', e.target.value)} /></Field>
          <Field label="Adress"><input style={inp} value={f.address} onChange={e => set('address', e.target.value)} /></Field>
          <Field label="Stad"><input style={inp} value={f.city} onChange={e => set('city', e.target.value)} /></Field>
          <Field label="Postnummer"><input style={inp} value={f.postalCode} onChange={e => set('postalCode', e.target.value)} /></Field>
        </div>
      </div>

      <div style={box}>
        <h3 className="font-bold mb-3">Tjänst och datum</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Tjänst">
            <select style={inp} value={f.service}
              onChange={e => {
                const v = e.target.value;
                setF(p => ({ ...p, service: v, rut: v !== 'office', subscription: SUB_SERVICES.includes(v) ? p.subscription : 'once' }));
              }}>
              {SERVICES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Abonnemang">
            <select style={inp} value={f.subscription} disabled={!SUB_SERVICES.includes(f.service)} onChange={e => set('subscription', e.target.value)}>
              {SUBS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Bostadsyta (kvm)"><input type="number" style={inp} value={f.sqm} onChange={e => set('sqm', e.target.value)} /></Field>
          <Field label="Timmar per person (Flytthjälp)"><input type="number" style={inp} value={f.hours} onChange={e => set('hours', e.target.value)} /></Field>
          <Field label="Tid"><input type="time" style={inp} value={f.time} onChange={e => set('time', e.target.value)} /></Field>
          <Field label="Status">
            <select style={inp} value={f.status} onChange={e => set('status', e.target.value)}>
              <option value="completed">Slutförd</option>
              <option value="upcoming">Kommande</option>
            </select>
          </Field>
        </div>

        <div className="mt-4">
          {isMulti ? (
            <div>
              <label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>
                Städdatum — ett datum per städning ({f.dates.length} st)
              </label>
              {f.dates.map((dt, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input type="date" style={inp} value={dt} onChange={e => setDate(i, e.target.value)} />
                  {f.dates.length > 1 && (
                    <button type="button" onClick={() => removeDate(i)} className="px-3 rounded-xl font-bold" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>✕</button>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={addDate} style={smallBtn}>+ Lägg till datum</button>
                <button type="button" onClick={fillDates} style={smallBtn}>Fyll i {autoCount} datum automatiskt</button>
              </div>
              <p className="text-xs mt-2" style={{ color: '#888' }}>
                {isPackage ? 'Priset gäller hela månaden oavsett antal datum.' : 'Priset = pris per städning × antal datum.'}
              </p>
            </div>
          ) : (
            <Field label="Datum (kan vara bakåt i tiden)">
              <input type="date" style={inp} value={f.dates[0] || ''} onChange={e => setDate(0, e.target.value)} />
            </Field>
          )}
        </div>
      </div>

      <div style={box}>
        <h3 className="font-bold mb-3">Tillägg och information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Ugnsrengöring (antal)"><input type="number" style={inp} value={f.ovenCount} onChange={e => set('ovenCount', e.target.value)} /></Field>
          <Field label="Kylskåpsrengöring (antal)"><input type="number" style={inp} value={f.fridgeCount} onChange={e => set('fridgeCount', e.target.value)} /></Field>
          <Field label="Fönsterputs (belopp kr — för tjänsten Fönsterputs är detta priset)"><input type="number" style={inp} value={f.windowKr} onChange={e => set('windowKr', e.target.value)} /></Field>
          <Field label="Fönster: antal och typ"><input style={inp} placeholder="t.ex. 8 st dubbelsidiga + karmtvätt" value={f.windowNote} onChange={e => set('windowNote', e.target.value)} /></Field>
          <Field label="Övriga tillägg (belopp kr)"><input type="number" style={inp} value={f.otherKr} onChange={e => set('otherKr', e.target.value)} /></Field>
          <Field label="Övriga tillägg: beskrivning"><input style={inp} value={f.otherNote} onChange={e => set('otherNote', e.target.value)} /></Field>
          <Field label="Nyckelhantering">
            <select style={inp} value={f.keyHandling} onChange={e => set('keyHandling', e.target.value)}>
              <option value="home">Kunden är hemma och öppnar</option>
              <option value="dropoff">Nyckel lämnas på kontoret</option>
              <option value="already">Nycklar redan lämnade</option>
            </select>
          </Field>
          <Field label="Husdjur (vilka)"><input style={inp} placeholder="t.ex. katt" value={f.petText} onChange={e => set('petText', e.target.value)} /></Field>
        </div>
        <div className="flex flex-wrap gap-6 mt-3 text-sm">
          <label><input type="checkbox" checked={f.needsEquipment} onChange={e => set('needsEquipment', e.target.checked)} /> Saknar städredskap (+400 kr)</label>
          <label><input type="checkbox" checked={f.hasPets} onChange={e => set('hasPets', e.target.checked)} /> Har husdjur</label>
        </div>
      </div>

      <div style={box}>
        <h3 className="font-bold mb-3">Pris och rabatt</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={`Eget pris före rabatt (tomt = ${autoPrice} kr automatiskt)`}>
            <input type="number" style={inp} value={priceOverride} onChange={e => setPriceOverride(e.target.value)} />
          </Field>
          <Field label="Rabatt i procent"><input type="number" style={inp} value={f.discountPercent} onChange={e => set('discountPercent', e.target.value)} /></Field>
          <Field label="Rabattkod (namn)">
            <div className="flex gap-2">
              <input style={inp} value={f.discountCode} onChange={e => set('discountCode', e.target.value)} />
              <button type="button" onClick={lookupCode} style={smallBtn}>Hämta %</button>
            </div>
          </Field>
        </div>
        {priceOverride !== '' && (
          <p className="text-xs mt-2" style={{ color: '#92400e' }}>
            Priset är låst till {priceOverride} kr.{' '}
            <button type="button" className="underline font-bold" onClick={() => setPriceOverride('')}>Räkna om automatiskt</button>
          </p>
        )}
        <label className="block mt-3 text-sm"><input type="checkbox" checked={f.rut} onChange={e => set('rut', e.target.checked)} /> RUT-avdrag 50%</label>
        <div className="mt-4 p-4 rounded-xl text-sm space-y-1" style={{ backgroundColor: '#1a1a2e', color: 'rgba(255,255,255,0.8)' }}>
          {visits > 1 && <div className="flex justify-between"><span>Antal städningar</span><span>{visits} st</span></div>}
          <div className="flex justify-between"><span>Pris före rabatt</span><span>{Math.round(priceBefore).toLocaleString()} kr</span></div>
          <div className="flex justify-between"><span>Efter rabatt ({dPct}%)</span><span>{Math.round(price).toLocaleString()} kr</span></div>
          <div className="flex justify-between"><span>RUT-avdrag</span><span>-{Math.round(rutAmount).toLocaleString()} kr</span></div>
          <div className="flex justify-between font-bold text-lg" style={{ color: '#C9A84C' }}><span>Kunden betalar</span><span>{Math.round(finalPrice).toLocaleString()} kr</span></div>
        </div>
      </div>

      <div style={box}>
        <h3 className="font-bold mb-3">Vem utförde städningen?</h3>
        <div className="flex flex-wrap gap-3 mb-3">
          {employees.map(e => (
            <label key={e.id} className="text-sm px-3 py-2 rounded-xl" style={{ border: `2px solid ${f.staffIds.includes(e.id) ? '#C9A84C' : '#e8e4d9'}` }}>
              <input type="checkbox" checked={f.staffIds.includes(e.id)} onChange={() => toggleStaff(e.id)} /> {e.name}
            </label>
          ))}
        </div>
        <Field label="Arbetstid per person och städning (timmar) — läggs i lönelistan för varje datum, 165 kr/h">
          <input type="number" step="0.5" style={inp} value={f.staffHours} onChange={e => set('staffHours', e.target.value)} />
        </Field>
      </div>

      <div style={box}>
        <Field label="Anteckning">
          <textarea rows={3} style={{ ...inp, resize: 'none' }} value={f.notes} onChange={e => set('notes', e.target.value)} />
        </Field>
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={onCancel} className="flex-1 py-3 rounded-full font-bold" style={{ minWidth: '120px', backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>Avbryt</button>
        <button onClick={() => save(true)} disabled={saving} className="flex-1 py-3 rounded-full font-bold" style={{ minWidth: '160px', backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
          {saving ? 'Sparar...' : 'Spara & Skicka Till Kund'}
        </button>
        <button onClick={() => save(false)} disabled={saving} className="flex-1 py-3 rounded-full font-bold" style={{ minWidth: '120px', backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
          {saving ? 'Sparar...' : 'Spara'}
        </button>
      </div>
    </div>
  );
};

export default ManualBookingForm;
